import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { mockAssets } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import { FiImage, FiVideo, FiArchive, FiFigma, FiMoreVertical, FiDownload, FiPlus, FiCheck } from 'react-icons/fi';

export default function AssetLibrary() {
  const [filter, setFilter] = useState('All');
  const [uploading, setUploading] = useState(false);

  const filteredAssets = mockAssets.filter(asset => {
    if (filter === 'All') return true;
    if (filter === 'Images') return asset.type === 'image';
    if (filter === 'Videos') return asset.type === 'video';
    if (filter === 'Documents') return asset.type === 'archive' || asset.type === 'design';
    return true;
  });

  const handleUpload = () => {
    setUploading(true);
    setTimeout(() => setUploading(false), 2000);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'image': return FiImage;
      case 'video': return FiVideo;
      case 'archive': return FiArchive;
      case 'design': return FiFigma;
      default: return FiImage;
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-8 max-w-7xl mx-auto h-full overflow-y-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div className="flex gap-2 bg-[#0a0a0a] p-1 rounded-xl border border-[#1a1a1a]">
          {['All', 'Images', 'Videos', 'Documents'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-4 py-1.5 rounded-lg transition-all ${
                filter === f 
                ? 'bg-[#FF5A5F] text-white shadow-lg' 
                : 'text-gray-500 hover:text-white'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <button 
          onClick={handleUpload}
          disabled={uploading}
          className="btn-primary flex items-center gap-2 min-w-[140px] justify-center"
        >
          {uploading ? (
            <><motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }}><SafeIcon icon={FiPlus} /></motion.div> Uploading...</>
          ) : (
            <><SafeIcon icon={FiPlus} /> Upload Asset</>
          )}
        </button>
      </div>

      <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <AnimatePresence mode="popLayout">
          {filteredAssets.map((asset) => (
            <motion.div
              key={asset.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              whileHover={{ y: -5 }}
              className="glass-panel group overflow-hidden cursor-pointer border-[#1a1a1a] hover:border-[#FF5A5F]/30"
            >
              <div className="h-40 bg-[#1a1a1a] relative overflow-hidden">
                {asset.type === 'image' || asset.type === 'video' ? (
                  <img src={asset.url} alt={asset.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <SafeIcon icon={getIcon(asset.type)} className="text-4xl text-gray-600 group-hover:text-[#FF5A5F] transition-colors" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="p-1.5 bg-black/50 backdrop-blur rounded text-white hover:bg-[#FF5A5F]"><SafeIcon icon={FiDownload} size={14} /></button>
                </div>
              </div>
              <div className="p-4 flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-white truncate w-40" title={asset.name}>{asset.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{asset.size}</p>
                </div>
                <button className="text-gray-500 hover:text-white"><SafeIcon icon={FiMoreVertical} /></button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}